import chroma from "chroma-js";
import Frame from "./Frame";

type TouchCoord = { x: number; y: number };
type LayerData = {
  _id: string;
  _fps: number;
  _offset: [number, number];
  _rotation: number;
  _scale: number;
  fill: boolean;
  frame: number;
  frames: Array<Frame>;
  playing: boolean;
  show_skins: boolean;
  skins: [number, number];
  width: number;
};

export default class Layer {
  static clonedProps: Array<string> = [
    "skins",
    "width",
    "fill",
    "playing",
    "_fps",
    "frame",
    "show_skins",
    "_offset",
    "_rotation",
    "_scale",
  ];
  static jsonProps: Array<string> = [
    "_id",
    "skins",
    "width",
    "fill",
    "_fps",
    "frame",
    "show_skins",
    "_offset",
    "rotation",
    "scale",
  ];

  active: boolean = false;
  selected: boolean = false;
  skins: [number, number] = [-2, 2];
  frames: Array<Frame>;

  color: string = chroma.random().hex();
  width: number = 5;
  fill: boolean = false;
  cycles: number = 0;

  playing: boolean = false;

  get currentFrame() {
    return this.frames[this.frame];
  }

  private _fps: number = 12;
  private _frame_rate: number = 1000 / this._fps;
  private _id: string;

  frame = 0;
  private _drawing: boolean = false;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  private last_frame_change: DOMHighResTimeStamp = performance.now();
  private show_skins: boolean = true;

  private last_pos: [number, number] = [0, 0];
  private _offset: [number, number] = [0, 0];

  set offset(pos: [number, number]) {
    this._offset = pos;
    this.canvas && this.render();
  }
  get offset() {
    return this._offset;
  }

  private _rotation: number = 0;

  set rotation(r) {
    this._rotation = r;
    this.canvas && this.render();
  }
  get rotation() {
    return this._rotation;
  }

  private _scale: number = 1.0;

  set scale(s) {
    this._scale = s;
    this.canvas && this.render();
  }
  get scale() {
    return this._scale;
  }

  get id() {
    return this._id;
  }
  get el() {
    return this.canvas;
  }
  get fps() {
    return this._fps;
  }
  set fps(to) {
    this._fps = to > 0 ? to : this._fps;
    this._frame_rate = 1000 / this._fps;
  }
  get frame_rate() {
    return this._frame_rate;
  }
  get drawing() {
    return this._drawing;
  }
  get rgb() {
    // Stupid way to get rgb values for rgba on onion skins
    // TODO: make a bit better
    return this.color
      .substr(1)
      .split("")
      .reduce(
        ([r, g, b], s, i) => [
          i < 2 ? r + s : r,
          i > 1 && i < 4 ? g + s : g,
          i > 3 ? b + s : b,
        ],
        ["", "", ""],
      )
      .map((v) => parseInt(v, 16));
  }

  get thumbnail() {
    return this.canvas.toDataURL();
  }

  static restore(container, data) {
    return new Layer(container, data);
  }

  constructor(
    container = document.body,
    saveData: LayerData = { _id: undefined, frames: undefined } as any,
  ) {
    const { _id, frames, ...props } = saveData;

    this._id = _id || crypto.getRandomValues(new Uint16Array(4)).join("-");
    this.frames = frames
      ? frames.map((f) => new Frame(f.paths, f.activePath))
      : new Array(12).fill(0).map(() => new Frame());

    if (props) Object.assign(this, props);

    this.canvas = document.createElement("canvas");
    this.canvas.height = container.offsetHeight;
    this.canvas.width = container.offsetWidth;

    this.canvas.addEventListener("pointerdown", this.handleDown);
    this.canvas.addEventListener("pointermove", this.handleMove);
    this.canvas.addEventListener("pointerup", this.handleUp);

    this.ctx = this.canvas.getContext("2d");

    container.appendChild(this.canvas);
  }

  destroy() {
    this.canvas.removeEventListener("pointerdown", this.handleDown);
    this.canvas.removeEventListener("pointermove", this.handleMove);
    this.canvas.removeEventListener("pointerup", this.handleUp);
    this.frames.forEach((frame) => frame.destroy());
    this.frames = undefined;
    this.ctx = undefined;
    this.canvas.parentNode.removeChild(this.canvas);
    this.canvas = undefined;
  }

  clone() {
    const data = Layer.clonedProps.reduce(
      (cloneData, p) => ({ ...cloneData, [p]: this[p] }),
      { frames: this.frames.map((frame) => frame.clone()) },
    ) as LayerData;

    const cloned = new Layer(this.canvas.parentElement, data);
    cloned.render();
    return cloned;
  }

  pointTransform(x, y) {
    try {
      // Apple sucks, and there's a proportional term added to the position of a
      // pointer, to compensate for their stupid scrolling behaviors.
      // Actually, they double suck because they claim "parity" between iPad OS
      // Safari and Desktop Safari such that they have identical user-agents
      // which means we have to create this stupid touch event on all platforms.
      document.createEvent("TouchEvent");
      y -= (60 * y) / this.canvas.height;
    } catch (e) {}

    return [
      (((x - this.canvas.width / 2) * 1) / this.scale) *
        Math.cos(-this.rotation) -
        (((y - this.canvas.height / 2) * 1) / this.scale) *
          Math.sin(-this.rotation),

      (((y - this.canvas.height / 2) * 1) / this.scale) *
        Math.cos(-this.rotation) +
        (((x - this.canvas.width / 2) * 1) / this.scale) *
          Math.sin(-this.rotation),
    ];
  }

  select() {
    this.selected = !this.selected;
  }

  activate() {
    this.active = !this.active;
    this.canvas.classList.toggle("active", this.active);
    this.render();
  }

  goTo(frame: number) {
    this.frame = frame;
    this.render();
    return this;
  }

  addFrame(at = this.frame) {
    this.frames.splice(at, 0, new Frame());
    this.render();
  }

  removeFrame(at = this.frame) {
    this.frames.splice(at, 1);
    this.render();
  }

  undo(frameIndex = this.frame) {
    const path = this.frames[frameIndex].paths.pop();
    this.render();
    return path;
  }

  next() {
    const prev_frame = this.frame;

    if (this.frame + 1 >= this.frames.length) this.cycles += 1;

    this.frame = this.frame + 1 >= this.frames.length ? 0 : this.frame + 1;

    // THIS IS WHAT UPDATES PATH WHEN DRAWING LIVE
    if (this.drawing) {
      this.frames[prev_frame].endPath();
      this.frames[this.frame].beginPath(this.last_pos);
    }

    this.render();
    return this;
  }

  prev() {
    if (this.frame - 1 < 0) this.cycles -= 1;

    this.frame = this.frame - 1 < 0 ? this.frames.length - 1 : this.frame - 1;
    this.render();
    return this;
  }

  showSkins() {
    this.show_skins = true;
  }

  hideSkins() {
    this.show_skins = false;
  }

  nextFrame(ts) {
    if (ts - this.last_frame_change < this.frame_rate) return this;
    this.last_frame_change = ts;
    this.next();
    return this;
  }

  handleDown = ({ clientX, clientY, pointerType }) => {
    if (pointerType === "touch" || !this.active) return;
    const [x, y] = this.pointTransform(clientX, clientY);

    this._drawing = true;
    this.canvas.style.zIndex = "100";
    this.frames[this.frame].beginPath([x - this.offset[0], y - this.offset[1]]);
    this.last_pos = [x, y];
  };

  handleMove = ({ clientX, clientY, pointerType }) => {
    if (pointerType === "touch" || !this.drawing) return;
    const [x, y] = this.pointTransform(clientX, clientY);

    // As user is drawing, we immediately render the new path
    this.ctx.beginPath();
    this.ctx.moveTo(...this.last_pos);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();

    this.frames[this.frame].lineTo([x - this.offset[0], y - this.offset[1]]);
    this.last_pos = [x, y];
    this.render();
  };

  handleUp = ({ clientX, clientY, pointerType }) => {
    if (pointerType === "touch") return;
    const [x, y] = this.pointTransform(clientX, clientY);

    this._drawing = false;
    // this.handleMove({ clientX: x, clientY: y });
    this.frames[this.frame].endPath([x - this.offset[0], y - this.offset[1]]);
    this.canvas.style.zIndex = null;
    this.render();
  };

  render(ctx = this.ctx) {
    if (ctx === this.ctx)
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    ctx.save();
    ctx.translate(this.canvas.width / 2, this.canvas.height / 2);
    ctx.scale(this.scale, this.scale);
    // TODO: The ordering of rotations/translations will screw up either the direction
    //       of translation (when translation is first) or it will screw up the offset of
    //       the drawing distance (when rotation is first)
    ctx.rotate(this.rotation);
    ctx.translate(...this.offset);

    // If we're on frame 0 with -N skin
    // If we're on last frame with +N skin
    let prev_frames = this.frame + this.skins[0];
    let next_frames = this.frame + this.skins[1];
    // const fade_decay = 1 / this.skins[0]; // TODO: look at non-linear cases
    ctx.lineWidth = this.width;
    // ONION SKINS (do not render while playing or while exporting to GIF)
    if (!this.playing && this.showSkins && ctx === this.ctx && this.active) {
      const [r, g, b] = this.rgb;
      ctx.fillStyle = ctx.strokeStyle = `rgba(${r},${g},${b},0.2)`;

      while (prev_frames < this.frame) {
        const idx =
          prev_frames < 0 ? this.frames.length + prev_frames : prev_frames;

        this.frames[idx].paths.forEach((path) => {
          ctx.beginPath();
          path.forEach(([x, y], i) => ctx[i ? "lineTo" : "moveTo"](x, y));
          if (this.fill) ctx.fill();
          ctx.stroke();
        });

        prev_frames += 1;
      }

      while (next_frames > this.frame) {
        const idx =
          next_frames >= this.frames.length
            ? next_frames - this.frames.length
            : next_frames;

        this.frames[idx].paths.forEach((path) => {
          ctx.beginPath();
          path.forEach(([x, y], i) => ctx[i ? "lineTo" : "moveTo"](x, y));
          if (this.fill) ctx.fill();
          ctx.stroke();
        });

        next_frames -= 1;
      }
    }

    ctx.fillStyle = ctx.strokeStyle = this.color;

    this.frames[this.frame].paths.forEach((path) => {
      ctx.beginPath();
      path.forEach(([x, y], i) => ctx[i ? "lineTo" : "moveTo"](x, y));
      if (this.fill) ctx.fill();
      ctx.stroke();
    });

    ctx.restore();
  }

  toJSON() {
    return Layer.jsonProps.reduce(
      (json, prop) => ({
        ...json,
        [prop]: this[prop],
      }),
      {
        frames: this.frames.map((f) => f.toJSON()),
      },
    );
  }
}
